import React, {createContext, useContext, useEffect, useMemo, useState} from "react";
import {I18nextProvider} from 'react-i18next';
import {API} from "../API";
import {AxiosResponse} from "axios";
import Request from "../Requests/Request";
import {User} from "../User";
import {LayoutContextProvider} from "./LayoutContext";
import {checkDarkMode} from "../../App";
import {UserContextProvider} from "./UserContext";
import {SystemContextProvider} from "./SystemContext";
import {ToastContextProvider} from "./ToastContext";
import i18n from "../../langs/translations";
import {useDispatch} from "react-redux";
import {changeLanguage} from "../../reducers/translations";
import {setupReconnect} from "../../hooks/reconnect";
import {APILink} from "../APILink";

export type ApplicationContextProviderProps = {
    request: Request;
    children?: React.ReactNode
}

const ApplicationContext = createContext<{
    request: Request;
    isPWA: boolean;
} | undefined>(undefined);

const useApplicationContext = () => {
    const context = useContext(ApplicationContext);

    if (context === undefined) {
        throw new Error("useApplicationContext was used outside of its Provider");
    }

    return context;
};

const ApplicationContextProvider = ({children, request}: ApplicationContextProviderProps) => {
    const dispatch = useDispatch();
    const [isPWA, setIsPWA] = useState(false);
    const [user, setUser] = useState<User>();
    const [error] = useState(null); // For throwing error from async function
    const [rendered] = useState<boolean>(false);
    if (error) {
        throw error;
    }

    useEffect(() => {
        setIsPWA(window.matchMedia('(display-mode: standalone)').matches || (window.navigator as any).standalone === true);
        if (request && request.theme) {
            localStorage.setItem('color-theme', request.theme.toLowerCase());
            checkDarkMode();
        }
        const makeInitRequest = () => {
            const userToken = localStorage.getItem('userToken') ?? null;
            if (userToken !== null) {
                API.me().then((response: AxiosResponse) => {
                    const userResponse = response.data.data as User;
                    setUser(userResponse);
                    setupReconnect();
                    if (userResponse.theme !== request.theme && !window.location.pathname.includes('/payment/')) {
                        localStorage.setItem('color-theme', userResponse.theme.toLowerCase());
                        checkDarkMode();
                    }
                    if (userResponse.language !== request.lang && !window.location.pathname.includes('/payment/')) {
                        dispatch(changeLanguage(userResponse.language));
                    }
                }).catch(() => {
                    localStorage.removeItem('userToken');
                    localStorage.removeItem('tokenExpiresAt');
                    window.location.replace('/app/admin');
                    API.removeURL(`get${APILink.ME}`);
                });
            }
            const brokerToken = localStorage.getItem('brokerToken') ?? null;
            if (brokerToken !== null) {
                API.brokerMe().then((response: AxiosResponse) => {
                    setUser(response.data);
                }).catch(() => {
                    localStorage.removeItem('brokerToken');
                    localStorage.removeItem('tokenExpiresAt');
                    API.removeURL(`get${APILink.BROKER_ME}`);
                    // window.location.replace('/app/admin');
                });
            }

            if (!localStorage.getItem('userToken') && !localStorage.getItem('brokerToken')
                && window.location.pathname.split('app/payment/').length === 1
                && window.location.pathname !== '/app/admin'
            ) {
                window.location.replace('/app/admin');
            }
        };

        makeInitRequest();
    }, [request, dispatch, changeLanguage]);

    return useMemo(() => {
        return <ApplicationContext.Provider value={{request, isPWA}}>
            <I18nextProvider i18n={i18n}>
                <LayoutContextProvider
                    theme={request.theme ?? 'light'}
                    render={{rerender: rendered}}
                    lang={request.lang ?? 'en'}
                >
                    <SystemContextProvider>
                        <ToastContextProvider>
                            {localStorage.getItem('userToken') && user ?
                                <UserContextProvider user={user}> {children} </UserContextProvider> : <>
                                    {children}
                                </>}
                        </ToastContextProvider>
                    </SystemContextProvider>
                </LayoutContextProvider>
            </I18nextProvider>
        </ApplicationContext.Provider>
    }, [rendered, user, isPWA])
};

export {useApplicationContext, ApplicationContextProvider};
